import { useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';

import {
  bindPopover,
  bindTrigger,
  usePopupState,
} from 'material-ui-popup-state/hooks';
import { isEmpty } from 'lodash';
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Pagination,
  Paper,
  Popover,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Edit, Error, FilterList, Home } from '@mui/icons-material';
import Http from '@/services/http';
import type { IPagination } from '@/models/pagination-model';
import type { IAircraftModel } from '@/models/aircraft-model-model';
import type { IAircraft } from '@/models/aircraft-model';
import { useDrawer } from '@/contexts/drawer';
import Header from '@/components/layouts/header';
import InfoLabel from '@/components/Info/label';
import FilterTags from '@/components/Info/filter-tags';

import AircraftFilterForm from './components/aircraft-filter-form';
import UpdateAircraftForm from '../update-aircraft';
import CreateAircraftForm from '../create-aircraft';

const ITEMS_PER_PAGE = 50;

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  cursor: 'pointer',
  transition: theme.transitions.create('background', {
    duration: theme.transitions.duration.short,
  }),
  '&:nth-of-type(odd), :hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const AircraftList: React.FC = () => {
  const drawer = useDrawer();

  const [searchParams, setSearchParams] = useSearchParams();

  const [aircrafts, setAircrafts] = useState<IPagination<IAircraft>>({
    docs: [],
    limit: 0,
    offset: 0,
    totalDocs: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
    pagingCounter: 0,
  });

  const [loadingAircrafts, setLoadingAircrafts] = useState<boolean>(false);

  const submitFilter = useCallback(
    async (values: Record<string, any>) => {
      Object.entries(values).forEach(([key, value]) => {
        let queryValue;
        if (value instanceof Date) {
          queryValue = value.toISOString();
        } else if (!isEmpty(value)) {
          queryValue = value;
          if (typeof value === 'object' && value._id) {
            queryValue = value._id;
          }
        }

        /* Check is value is a boolean */
        if (typeof value === 'boolean') {
          if (value) queryValue = 'true';
          else queryValue = undefined;
        }

        if (queryValue) {
          searchParams.set(key, queryValue);
        } else {
          searchParams.delete(key);
        }
      });

      if (!values.page) {
        searchParams.delete('page');
      }

      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  const fetchAircrafts = useCallback(async () => {
    setLoadingAircrafts(true);

    const { status, data } = await Http.get('aircrafts', {
      params: {
        ...Object.fromEntries(searchParams),
        limit: ITEMS_PER_PAGE,
      },
    });

    if (status === 200) setAircrafts(data);

    setLoadingAircrafts(false);
  }, [searchParams]);

  const [models, setModels] = useState<IAircraftModel[]>([]);

  const fetchModels = useCallback(async () => {
    const { data, status } = await Http.get('aircraft-models');

    if (status === 200) {
      setModels(data?.docs);
    }
  }, []);
  const createAircraft = () => {
    drawer.open({
      element: <CreateAircraftForm />,
      onClose(newAircraft: IAircraft) {
        if (newAircraft) {
          fetchAircrafts();
        }
      },
      options: {
        width: 500,
      },
    });
  };
  const updateAircraft = (aircraft: IAircraft) => {
    drawer.open({
      options: {
        width: 500,
      },
      element: <UpdateAircraftForm aircraft={aircraft} />,
      onClose(aircraftUpdated) {
        if (aircraftUpdated) fetchAircrafts();
      },
    });
  };

  useEffect(() => {
    fetchAircrafts();
    fetchModels();
  }, [fetchAircrafts, fetchModels]);

  const popupState = usePopupState({ variant: 'popover', popupId: 'filter' });

  return (
    <Stack spacing={1}>
      <Header
        breadcrumbs={[
          {
            icon: Home,
          },
          {
            text: 'Configurações',
          },
          {
            text: 'Aeronaves',
          },
        ]}
        headerChildren={
          <Stack alignItems="center" direction="row" spacing={1}>
            <FilterTags
              tagDefs={[
                {
                  paramKey: 'prefix',
                  valueGetter(value) {
                    return `Marca ${value}`;
                  },
                },
                {
                  paramKey: 'active',
                  valueGetter() {
                    return `Ativa`;
                  },
                },
                {
                  paramKey: '_model',
                  valueGetter(value) {
                    const model = models.find((i) => i._id === value);

                    if (!model) return `Carregando...`;

                    return `Modelo ${model.manufacturer} - ${model.model}`;
                  },
                },
                {
                  paramKey: 'sn',
                  valueGetter(value) {
                    return `S/N ${value}`;
                  },
                },
                {
                  paramKey: 'backup',
                  valueGetter() {
                    return `Backup`;
                  },
                },
              ]}
            />
            <IconButton color="secondary" {...bindTrigger(popupState)}>
              <FilterList />
            </IconButton>
            <Popover sx={{ marginRight: 2 }} {...bindPopover(popupState)}>
              <AircraftFilterForm
                onSubmit={async (values) => {
                  popupState.close();
                  submitFilter(values);
                }}
              />
            </Popover>
            <IconButton color="secondary" onClick={createAircraft}>
              <Add />
            </IconButton>
          </Stack>
        }
        title="Aeronaves"
      />
      <Paper sx={{ width: '100%' }}>
        <Collapse in={loadingAircrafts} unmountOnExit>
          <Box
            alignItems="center"
            bgcolor="grey.100"
            display="flex"
            justifyContent="center"
            p={4}
          >
            <CircularProgress />
          </Box>
        </Collapse>
        <Collapse in={!loadingAircrafts && aircrafts.docs.length === 0}>
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={2}
            sx={{ p: 2 }}
          >
            <Typography variant="caption">
              <Error sx={{ fontSize: '56px' }} />
            </Typography>
            <Typography color="GrayText" variant="h5">
              Nenhuma aeronave encontrada
            </Typography>
          </Stack>
        </Collapse>
        <Collapse
          in={!loadingAircrafts && aircrafts.docs.length > 0}
          unmountOnExit
        >
          <TableContainer sx={{ overflowY: 'auto' }}>
            <Table
              aria-label="aircraft list"
              sx={{ maxWidth: '100%', whiteSpace: 'nowrap' }}
            >
              <TableBody>
                {aircrafts.docs?.map((aircraft) => (
                  <StyledTableRow key={aircraft._id}>
                    <TableCell sx={{ width: '1%', pr: 0 }}>
                      <Tooltip title="Editar">
                        <IconButton onClick={() => updateAircraft(aircraft)}>
                          <Edit />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ maxWidth: '70px' }}>
                      <Stack alignItems="center">
                        {aircraft.active ? (
                          <Chip color="success" label="Ativa" />
                        ) : (
                          <Chip label="Não ativa" />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <InfoLabel
                        items={[
                          {
                            title: 'Modelo',
                            value: `${aircraft?._model?.manufacturer} - ${aircraft?._model?.model}`,
                          },
                        ]}
                      />
                    </TableCell>
                    <TableCell>
                      <InfoLabel
                        items={[
                          {
                            title: 'S/N',
                            value: `${aircraft.sn}`,
                          },
                        ]}
                      />
                    </TableCell>
                    <TableCell>
                      <InfoLabel
                        items={[
                          {
                            title: 'Marca',
                            value: aircraft.prefix,
                          },
                        ]}
                      />
                    </TableCell>
                    <TableCell>
                      <InfoLabel
                        items={[
                          {
                            title: 'Base',
                            value: aircraft?.base
                              ? `${aircraft?.base?.name} (${aircraft?.base?.code})`
                              : '-',
                          },
                        ]}
                      />
                    </TableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Collapse>
        {aircrafts.totalPages > 1 && (
          <Box sx={{ m: 2 }}>
            <Pagination
              count={aircrafts.totalPages}
              onChange={(_, selectedPage) =>
                submitFilter({ page: selectedPage.toString() })
              }
              page={aircrafts.offset}
            />
          </Box>
        )}
      </Paper>
    </Stack>
  );
};

export default AircraftList;
